import React from 'react';
import clsx from 'clsx';
import { useFetcher, useNavigate } from 'react-router-dom';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { PageTitle } from '@oysterjs/ui/page';
import { Text } from '@oysterjs/ui/text';

import type {
  EmailTemplateEditorFieldsFragment,
  EmailTemplateInput,
  UpdateEmailTemplateMutation
} from '~/api/graphql';
import { AddCaraSectionButton } from './add-cara-section-button';
import { CaraSectionNode } from './cara-section-node';
import { EmailTemplateFieldChips, fieldChipClassName } from './field-chips';
import { FormError } from '~/components/form-error';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment
} from '~/components/visibility/visibility-selector.utils';
import { dateFormatter } from '~/components/table-formatters';
import { HtmlDocumentEditor } from '~/pages/copilot/workspace/legacy-editor/html-document-editor';
import { DiscardChangesDialog } from './discard-changes-dialog';
import { EditAndPreviewPanes } from './edit-and-preview-panes';
import { EmailTemplateArchiveButton } from './email-template-archive-button';
import { EmailTemplatePreview } from './email-template-preview';
import { EmailTemplateReadOnlyNotice } from './email-template-read-only-notice';
import {
  EMAIL_TEMPLATES_PATH,
  NEW_EMAIL_TEMPLATE_PATH,
  emailTemplatePath
} from './email-template-paths';
import {
  SaveProblemNotice,
  saveProblemFrom,
  validationMessageFor
} from './email-template-save-problem';
import {
  GuidelineRowInputs,
  guidelinesToSave,
  useGuidelineRows
} from '~/components/settings/email/guideline-rows';

type EmailTemplate = EmailTemplateEditorFieldsFragment;

export const EmailTemplatePageTitle = (
  props: React.PropsWithChildren<{ title: string; description?: string; badge?: JSX.Element }>
) => <PageTitle eyebrow="Back to email templates" eyebrowHref={EMAIL_TEMPLATES_PATH} {...props} />;

const lastEditedDescription = (template: EmailTemplate) => {
  const editedOn = dateFormatter.format(new Date(template.updatedAt));
  const editedBy = template.updatedBy ? ` by ${template.updatedBy.name}` : '';
  return `Last edited${editedBy} on ${editedOn}. Changes apply to new drafts only.`;
};

// The editor's chips sit on white paper in both themes; this one sits on the card, which darkens.
const FieldChipExample = ({ children }: { children: string }) => (
  <span className={clsx(fieldChipClassName, 'dark:text-sky-300')}>{children}</span>
);

const editorTitle = (template: EmailTemplate | null, canEdit: boolean) => {
  if (!template) return 'New template';
  return canEdit ? 'Edit template' : template.name;
};

const templateTextFrom = (template: EmailTemplate | null) => ({
  name: template?.name ?? '',
  whenToUse: template?.whenToUse ?? '',
  subject: template?.subject ?? '',
  bodyHtml: template?.bodyHtml ?? ''
});

type EmailTemplateEditorProps = {
  /** Null for a template that hasn't been saved yet. */
  template: EmailTemplate | null;
};

export const EmailTemplateEditor = ({ template }: EmailTemplateEditorProps) => {
  const navigate = useNavigate();
  // A new template's route already requires permission to create one.
  const canEdit = template?.canEdit ?? true;
  const fetcher = useFetcher<ActionResult<UpdateEmailTemplateMutation>>();
  const isSaving = fetcher.state !== 'idle';
  const lastSave = isSaving ? undefined : fetcher.data;
  const saveProblem = saveProblemFrom(lastSave);
  const nameError = validationMessageFor(lastSave, 'name');
  const whenToUseError = validationMessageFor(lastSave, 'whenToUse');
  const subjectError = validationMessageFor(lastSave, 'subject');
  const bodyError = validationMessageFor(lastSave, 'bodyHtml');
  const guidelinesError = validationMessageFor(lastSave, 'guidelines');

  const [text, setText] = React.useState(() => templateTextFrom(template));
  // The body editor replaces what's typed whenever its content changes, so it only ever gets
  // the body first loaded, never a reload's.
  const [loadedBodyHtml] = React.useState(text.bodyHtml);
  const editText = (change: Partial<typeof text>) =>
    setText((current) => ({ ...current, ...change }));
  const guidelineRows = useGuidelineRows(template?.guidelines ?? []);
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState(() =>
    template ? visibilityValueFromFragment(template.visibility) : defaultVisibility
  );
  const canChooseVisibility = useVisibilityEntitlement();

  // The server trims these, so spaces at either end are no change.
  const draft = {
    name: text.name.trim(),
    whenToUse: text.whenToUse.trim(),
    subject: text.subject.trim(),
    bodyHtml: text.bodyHtml,
    guidelines: guidelinesToSave(guidelineRows.rows),
    visibility: visibilityInputFromValue(visibility)
  };
  // What the server holds as far as this editor knows. Only this editor's own saves move
  // it: a reload after someone else's save must not let the next save overwrite theirs.
  const [saved, setSaved] = React.useState(() => ({ version: template?.version ?? null, draft }));
  const hasUnsavedChanges = canEdit && JSON.stringify(draft) !== JSON.stringify(saved.draft);
  const submittedDraftRef = React.useRef(draft);
  const savedTemplate = isSaving ? undefined : fetcher.data?.data?.updateEmailTemplate;
  React.useEffect(() => {
    if (savedTemplate)
      setSaved({ version: savedTemplate.version, draft: submittedDraftRef.current });
  }, [savedTemplate]);
  const allowLeaveRef = React.useRef(false);

  // The editor reads its extensions once, when it is built.
  const [bodyExtensions] = React.useState(() => [EmailTemplateFieldChips, CaraSectionNode]);

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    submittedDraftRef.current = draft;
    // Named rather than spread, so a field this editor doesn't show is never sent back.
    const input: EmailTemplateInput = {
      name: draft.name,
      whenToUse: draft.whenToUse,
      subject: draft.subject,
      bodyHtml: draft.bodyHtml,
      guidelines: draft.guidelines,
      ...(canChooseVisibility && { visibility: draft.visibility })
    };
    const submitOptions = { encType: 'application/json' } as const;
    if (template) {
      fetcher.submit(
        { expectedVersion: saved.version, input },
        { ...submitOptions, method: 'put', action: emailTemplatePath(template.id) }
      );
    } else {
      fetcher.submit(
        { input },
        { ...submitOptions, method: 'post', action: NEW_EMAIL_TEMPLATE_PATH }
      );
    }
  };

  return (
    <EmailTemplatePageTitle
      title={editorTitle(template, canEdit)}
      description={template && canEdit ? lastEditedDescription(template) : undefined}
      badge={canEdit ? undefined : <Badge color="neutral">View only</Badge>}
    >
      <EditAndPreviewPanes
        edit={
          <fetcher.Form
            onSubmit={save}
            className="rounded-lg bg-white px-4 py-5 shadow sm:px-6 dark:bg-neutral-800"
          >
            {template && !canEdit && (
              <div className="mb-6">
                <EmailTemplateReadOnlyNotice template={template} />
              </div>
            )}
            <Fieldset disabled={!canEdit || isSaving}>
              <FieldGroup>
                {saveProblem && <SaveProblemNotice problem={saveProblem} />}
                <Field>
                  <Label>Template name</Label>
                  <Input
                    value={text.name}
                    invalid={!!nameError}
                    onChange={(event) => editText({ name: event.target.value })}
                  />
                  {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>When to use it (optional)</Label>
                  <Description>
                    Helps Cara suggest this template. Agents can still pick any template.
                  </Description>
                  <Input
                    value={text.whenToUse}
                    invalid={!!whenToUseError}
                    onChange={(event) => editText({ whenToUse: event.target.value })}
                  />
                  {whenToUseError && <ErrorMessage>{whenToUseError}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>Subject line</Label>
                  <Input
                    value={text.subject}
                    placeholder="e.g. Your [[carrier]] policy renews soon"
                    invalid={!!subjectError}
                    onChange={(event) => editText({ subject: event.target.value })}
                  />
                  {subjectError && <ErrorMessage>{subjectError}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>Email</Label>
                  <Description>
                    Type <FieldChipExample>[[first name]]</FieldChipExample> or any{' '}
                    <FieldChipExample>[[field]]</FieldChipExample> and Cara fills it in for each
                    email. Everything else outside a Cara section is sent word for word.
                  </Description>
                  <div data-slot="control" className="flex h-96 flex-col">
                    <HtmlDocumentEditor
                      ariaLabel="Email body"
                      content={loadedBodyHtml}
                      editable={canEdit && !isSaving}
                      showToolbar={canEdit}
                      showImageButton={false}
                      extensions={bodyExtensions}
                      toolbarControls={(editor) => <AddCaraSectionButton editor={editor} />}
                      onChange={(bodyHtml) => editText({ bodyHtml })}
                    />
                  </div>
                  {bodyError && <ErrorMessage>{bodyError}</ErrorMessage>}
                </Field>
                <Fieldset>
                  <Legend className="sm:text-sm/6">Guidelines for this template (optional)</Legend>
                  <Text>
                    Cara follows these when it writes the Cara sections, on top of your agency
                    guidelines.
                  </Text>
                  <div className="mt-2">
                    <GuidelineRowInputs guidelineRows={guidelineRows} canEdit={canEdit} />
                  </div>
                  <FormError
                    message={guidelinesError}
                    className="mt-2 text-sm/5 text-red-600 sm:text-xs/4 dark:text-red-500"
                  />
                </Fieldset>
              </FieldGroup>
            </Fieldset>
            {canEdit && (
              <div className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                {template && (
                  <EmailTemplateArchiveButton
                    template={template}
                    onArchived={() => {
                      allowLeaveRef.current = true;
                      navigate(EMAIL_TEMPLATES_PATH);
                    }}
                  />
                )}
                <Button color="white" href={EMAIL_TEMPLATES_PATH}>
                  Cancel
                </Button>
                <VisibilitySplitButton
                  type="submit"
                  value={visibility}
                  onChange={setVisibility}
                  loading={isSaving}
                >
                  {saveProblem?.canRetry ? 'Try saving again' : 'Save template'}
                </VisibilitySplitButton>
              </div>
            )}
          </fetcher.Form>
        }
        preview={<EmailTemplatePreview subject={text.subject} bodyHtml={text.bodyHtml} />}
      />
      <DiscardChangesDialog hasUnsavedChanges={hasUnsavedChanges} allowLeaveRef={allowLeaveRef} />
    </EmailTemplatePageTitle>
  );
};
